import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DocsLayout, DocsCard, DocsContent, MarkdownContent } from "../components/docs";
import { docsNavigation } from "../config/docs";

function DocsHub() {
  const { t } = useTranslation();

  const popularTopics = [
    { labelKey: "docs.topics.quickStart", path: "/docs/getting-started/quick-start" },
    { labelKey: "docs.topics.installation", path: "/docs/getting-started/installation" },
    { labelKey: "docs.topics.projects", path: "/docs/concepts/projects" },
    { labelKey: "docs.topics.sslHttps", path: "/docs/guides/ssl-https" },
    { labelKey: "docs.topics.cliReference", path: "/docs/reference/cli" },
  ];

  return (
    <DocsLayout hideSidebar>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-warm-50 mb-4">{t("docs.title")}</h1>
          <p className="text-lg text-warm-400 max-w-2xl mx-auto">
            {t("docs.subtitle")}
          </p>
        </div>

        <div className="mb-12">
          <div className="relative max-w-xl mx-auto">
            <input
              type="text"
              placeholder={t("docs.searchPlaceholder")}
              disabled
              className="w-full px-4 py-3 pl-11 bg-dark-card border border-dark-border rounded-lg text-warm-100 placeholder-warm-600 cursor-not-allowed opacity-60"
            />
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-semibold text-warm-100 mb-4">{t("docs.popularTopics")}</h2>
          <div className="flex flex-wrap gap-2">
            {popularTopics.map((topic) => (
              <Link
                key={topic.labelKey}
                to={topic.path}
                className="px-3 py-1.5 bg-dark-card border border-dark-border rounded-full text-sm text-warm-400 hover:text-warm-100 hover:border-warm-600 transition-colors"
              >
                {t(topic.labelKey)}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {docsNavigation.map((category) => (
            <DocsCard key={category.id} category={category} />
          ))}
        </div>
      </div>
    </DocsLayout>
  );
}

function DocPage() {
  const { t } = useTranslation();
  const params = useParams();
  const path = params["*"] || "";
  const [category, slug] = path.split("/");

  const categoryData = docsNavigation.find((c) => c.id === category);
  const currentIndex = categoryData?.items.findIndex((i) => i.slug === slug) ?? -1;
  const prevDoc = currentIndex > 0 ? categoryData?.items[currentIndex - 1] : undefined;
  const nextDoc =
    currentIndex >= 0 && currentIndex < (categoryData?.items.length ?? 0) - 1
      ? categoryData?.items[currentIndex + 1]
      : undefined;

  return (
    <DocsLayout>
      <DocsContent>
        <MarkdownContent path={`/content/docs/${category}/${slug}.md`} />
      </DocsContent>

      {(prevDoc || nextDoc) && (
        <div className="flex justify-between items-center pt-8 mt-8 border-t border-dark-border max-w-3xl">
          {prevDoc ? (
            <Link
              to={`/docs/${category}/${prevDoc.slug}`}
              className="group flex items-center gap-2 text-warm-400 hover:text-warm-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <div>
                <div className="text-xs text-warm-600">{t("docs.nav.previous")}</div>
                <div className="text-sm font-medium">{t(prevDoc.titleKey)}</div>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {nextDoc ? (
            <Link
              to={`/docs/${category}/${nextDoc.slug}`}
              className="group flex items-center gap-2 text-warm-400 hover:text-warm-100 transition-colors text-right"
            >
              <div>
                <div className="text-xs text-warm-600">{t("docs.nav.next")}</div>
                <div className="text-sm font-medium">{t(nextDoc.titleKey)}</div>
              </div>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          ) : (
            <div />
          )}
        </div>
      )}
    </DocsLayout>
  );
}

export function Docs() {
  const params = useParams();
  const path = params["*"];

  if (!path) {
    return <DocsHub />;
  }

  return <DocPage />;
}
